import React, {useCallback, useMemo, memo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {MoreVertical} from 'lucide-react';

import {GUEST_STATUSES, ShortGuest, useTimeoutRef} from '@chekin/core';
import {cn, GuestBox, useModalControls} from '@chekin/guestapp-ui-kit';
import {PATHS} from 'routes/constants';
import {useErrorModal, useIVStore} from '@chekin/shared';
import {useAuth} from 'hooks/auth/useAuth';
import {useDeleteGuest} from 'hooks/useDeleteGuest';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {usePath} from 'hooks';
import {IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import Dropdown from 'components/common/Dropdown';
import IconButton from 'components/common/IconButton';
import {GuestStatusesTexts} from 'components/GuestsPanel/types';
import {GuestPanelItem} from '../GuestPanelItem';
import {DeleteGuestStatusModal} from './DeteleGuestStatusModal';
import {Texts, Title, StatusText, StatusIcon, DotsButton} from './styled';
import ChekinProSettings from 'ChekinProSettings';

import completeIcon from '../../../../assets/icons/complete-icon.svg?no-inline';
import incompleteIcon from '../../../../assets/icons/incomplete-icon.svg?no-inline';

const guestStatusText: GuestStatusesTexts = {
  [GUEST_STATUSES.complete]: 'completed',
  [GUEST_STATUSES.verification_pending]: 'verification_pending',
  [GUEST_STATUSES.incomplete]: 'registration_pending',
};

const DELETE_GUEST_MODAL_TIMEOUT = 3000;

type Props = {
  guestMember: ShortGuest & {isLeader?: boolean};
  isLoading?: boolean;
  isNew?: boolean;
  onRegisterNew?: () => void;
};
const GuestItem = memo(({guestMember, isLoading, isNew, onRegisterNew}: Props) => {
  const {getMainPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const {ErrorModal, displayError} = useErrorModal();
  const navigate = useNavigate();
  const {isLoggedIn} = useAuth();
  const closeModalTimeoutRef = useTimeoutRef();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {t} = useTranslation();
  const {mutate: deleteGuestMutate, status: deleteGuestStatus} = useDeleteGuest({
    onError: displayError,
    onMutate: openModal,
    onSuccess: () => {
      closeModalTimeoutRef.current = setTimeout(() => {
        closeModal();
      }, DELETE_GUEST_MODAL_TIMEOUT);
    },
  });

  const {id: guestId, full_name, guestapp_status, isLeader} = guestMember;
  const completed = guestapp_status === GUEST_STATUSES.complete;
  const statusIcon = completed ? completeIcon : incompleteIcon;
  const statusText = guestStatusText[guestapp_status] || '';
  const isEditable = !completed;

  const {isIVOnPropertyAvailable} = useIVOnProperty();
  const getLinkToUpdateGuestProcessView = useCallback(
    (guest?: ShortGuest) => {
      const guestId = guest?.id;
      const guestStatus = guest?.guestapp_status;
      const isVerificationPending = guestStatus === GUEST_STATUSES.verification_pending;
      const isPreRegisteredAndInCompleted =
        guest?.is_pre_registered && guestStatus === GUEST_STATUSES.incomplete;
      if (guestId) {
        if (isVerificationPending) {
          return isIVOnPropertyAvailable
            ? getMainPath(
                `${PATHS.verification.main}/${guestId}/${PATHS.verification.qrScan}`,
              )
            : getMainPath(`${PATHS.verification.main}/${guestId}`);
        }
        if (isPreRegisteredAndInCompleted || isLoggedIn) {
          const isLeaderFlag = `${IS_LEADER_GUEST_STORAGE_KEY}=${isLeader}`;
          return getMainPath(
            `${PATHS.addPersonalDataForm}/${guestId}${
              isLeader ? '?' + isLeaderFlag : ''
            }`,
          );
        }
      }
    },
    [getMainPath, isIVOnPropertyAvailable, isLeader, isLoggedIn],
  );

  const gotToEdit = useCallback(() => {
    const link = getLinkToUpdateGuestProcessView(guestMember);

    if (isEditable && link) {
      resetAllIVState();
      navigate(link);
    }
  }, [
    getLinkToUpdateGuestProcessView,
    guestMember,
    isEditable,
    navigate,
    resetAllIVState,
  ]);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if ((isEditable || ChekinProSettings.enableGuestsRemoval) && guestMember.id) {
        deleteGuestMutate(guestMember.id);
      }
    },
    [deleteGuestMutate, isEditable, guestMember.id],
  );

  const handleGuestClick = useCallback(() => {
    if (full_name) {
      gotToEdit();
      return;
    }
    onRegisterNew?.();
  }, [full_name, gotToEdit, onRegisterNew]);

  const dropdownOptions = useMemo(() => {
    return [
      // {
      //   id: 1,
      //   label: t('fill_in_incomplete_data'),
      //   onClick: () => {
      //     console.log('remove item');
      //   },
      // },
      {
        id: 2,
        label: t('edit_guest'),
        onClick: gotToEdit,
        hidden: !isEditable,
      },
      {
        id: 3,
        label: t('delete_guest'),
        onClick: handleDelete,
        hidden: !(isEditable || ChekinProSettings.enableGuestsRemoval),
      },
    ];
  }, [gotToEdit, handleDelete, isEditable, t]);

  const showActions = dropdownOptions.some(action => !action.hidden);

  return (
    <>
      {isNew ? (
        <GuestBox
          hasGuest={!!guestId}
          guestName={full_name || t('unknown_guest')}
          completed={completed}
          loading={isLoading}
          status={t(statusText)}
          showAddBtn={!guestId}
          onClick={handleGuestClick}
          actionsButton={
            !!guestId && showActions ? (
              <Dropdown
                position="bottom right"
                options={dropdownOptions}
                disabled={isLoading}
                trigger={
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <MoreVertical className="w-5 h-5" />
                  </div>
                }
              />
            ) : undefined
          }
        />
      ) : (
        <GuestPanelItem
          $isLoading={isLoading}
          $completed={completed}
          className={cn('guests-panel__item', 'kiosko:p-[38px]')}
        >
          <Texts className="kiosko:gap-4 kiosko:flex kiosko:flex-col">
            <Title className={cn('guests-panel__title', 'kiosko:text-kiosko-s')}>
              {full_name}
            </Title>
            <StatusText
              className={cn('guests-panel__status', 'kiosko:text-kiosko-xs')}
              $completed={completed}
            >
              <StatusIcon src={statusIcon} width={14} />
              {t(statusText)}
            </StatusText>
          </Texts>
          {showActions && (
            <Dropdown
              position="bottom right"
              options={dropdownOptions}
              disabled={isLoading}
              trigger={
                <div>
                  <IconButton icon={<DotsButton name="dots" size={16} />} />
                </div>
              }
            />
          )}
          <ErrorModal />
          <DeleteGuestStatusModal open={isOpen} state={deleteGuestStatus} />
        </GuestPanelItem>
      )}
    </>
  );
});

export {GuestItem};
